import React, { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck, RotateCcw } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const POLL_MS = 30000;

const TYPE_ICON = {
  requisition_created: "🔔",
  requisition_approved: "✅",
  requisition_rejected: "⛔",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const loadCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch (_) { /* silent */ }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=15");
      setItems(data || []);
    } catch (_) { setItems([]); }
    setLoading(false);
  };

  useEffect(() => {
    // Poll only when tab is visible; pause when hidden to save bandwidth.
    let timer = null;

    const startPolling = () => {
      if (timer) return;
      loadCount();
      timer = setInterval(loadCount, POLL_MS);
    };
    const stopPolling = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVis = () => {
      if (document.hidden) stopPolling(); else startPolling();
    };

    // Initial state
    if (!document.hidden) startPolling();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      stopPolling();
    };
  }, []);

  useEffect(() => {
    if (open) loadItems();
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setItems((cur) => cur.map((i) => i.id === id ? { ...i, read: true } : i));
      loadCount();
    } catch (_) { /* silent */ }
  };

  const markUnread = async (e, id) => {
    e.stopPropagation();
    try {
      await api.put(`/notifications/${id}/unread`);
      setItems((cur) => cur.map((i) => i.id === id ? { ...i, read: false } : i));
      loadCount();
    } catch (_) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((cur) => cur.map((i) => ({ ...i, read: true })));
      setCount(0);
    } catch (_) { /* silent */ }
  };

  const openItem = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 border-2 border-[#111] bg-white hover:bg-[#FFF8E1] transition-colors"
        title="Notificações"
        data-testid="notification-bell"
      >
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 bg-[#FF4F00] text-white text-[10px] font-mono-data font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center border-2 border-white"
            data-testid="notification-count"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white border-2 border-[#111] shadow-2xl z-50" data-testid="notification-dropdown">
          <div className="px-3 py-2 border-b-2 border-[#111] flex items-center justify-between bg-[#0A1F14] text-white">
            <div className="font-mono-data text-[10px] uppercase tracking-widest">Notificações</div>
            {count > 0 && (
              <button
                onClick={markAllRead}
                className="text-[10px] font-mono-data uppercase tracking-widest hover:text-[#FF9F5A] flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3 h-3" /> Marcar todas
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading && <div className="p-4 text-center font-mono-data text-xs text-[#777]">Carregando...</div>}
            {!loading && items.length === 0 && (
              <div className="p-6 text-center font-mono-data text-xs text-[#777]">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                Sem notificações
              </div>
            )}
            {!loading && items.map((n) => (
              <div
                key={n.id}
                className={`w-full border-b border-[#EEE] hover:bg-[#FFF8E1] transition-colors flex items-stretch group ${n.read ? "opacity-60" : "bg-[#FFFBEA]"}`}
                data-testid={`notification-item-${n.id}`}
              >
                <button
                  onClick={() => openItem(n)}
                  className="flex-1 text-left px-3 py-2"
                >
                  <div className="flex items-start gap-2">
                    <div className="text-lg leading-none pt-0.5">{TYPE_ICON[n.tipo] || "🔔"}</div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${n.read ? "" : "font-semibold"} text-[#111]`}>{n.titulo}</div>
                      {n.mensagem && <div className="text-xs text-[#555] mt-0.5">{n.mensagem}</div>}
                      <div className="font-mono-data text-[9px] text-[#999] uppercase tracking-widest mt-1">
                        {fmtDate(n.created_at)}
                      </div>
                    </div>
                    {!n.read && <div className="w-2 h-2 bg-[#FF4F00] rounded-full mt-1.5 shrink-0" />}
                  </div>
                </button>
                {n.read && (
                  <button
                    onClick={(e) => markUnread(e, n.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity px-2 hover:bg-[#0A1F14] hover:text-white flex items-center justify-center border-l border-[#EEE]"
                    title="Marcar como não lida"
                    data-testid={`mark-unread-${n.id}`}
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
